import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Info, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';

export const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export const MetricCard = ({ title, value, icon: Icon, subtitle, className = '' }) => (
  <Card className={`border-t-4 border-t-gold-500 bg-white hover:shadow-md transition-shadow duration-300 ${className}`} data-testid={`metric-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-stone-500 uppercase tracking-wider mb-1">{title}</p>
          <p className="text-3xl font-bold text-stone-900 tracking-tight font-sans">{value}</p>
          {subtitle && <p className="text-sm text-stone-400 mt-1">{subtitle}</p>}
        </div>
        <div className="p-3 bg-gold-50 rounded-lg">
          <Icon className="w-6 h-6 text-gold-600" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export const PercentageBadge = ({ value }) => {
  let badgeClass = 'bg-stone-50 text-stone-400 border-stone-100';

  if (value >= 10) {
    badgeClass = 'bg-gold-50 text-gold-700 border-gold-200';
  } else if (value >= 5) {
    badgeClass = 'bg-stone-100 text-stone-600 border-stone-200';
  }

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border ${badgeClass}`}>
      {value.toFixed(1)}%
    </span>
  );
};

export const WeightBar = ({ value, max }) => {
  const percentage = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="w-full bg-stone-100 rounded-full h-1.5">
      <div
        className="bg-gold-500 h-1.5 rounded-full transition-all duration-500"
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
};

export const LogEntry = ({ log }) => {
  const icons = {
    info: <Info className="w-4 h-4 text-blue-500" />,
    success: <CheckCircle className="w-4 h-4 text-green-500" />,
    warning: <AlertTriangle className="w-4 h-4 text-amber-500" />,
    error: <XCircle className="w-4 h-4 text-red-500" />
  };

  return (
    <div className="flex items-start gap-3 py-2 border-b border-stone-100 last:border-0">
      <div className="mt-0.5">{icons[log.type] || icons.info}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-stone-700">{log.message}</p>
        {log.details && (
          <pre className="mt-1 text-xs text-stone-500 bg-stone-50 p-2 rounded overflow-x-auto">
            {typeof log.details === 'object' ? JSON.stringify(log.details, null, 2) : log.details}
          </pre>
        )}
      </div>
      <span className="text-xs text-stone-400 whitespace-nowrap">
        {new Date(log.timestamp).toLocaleTimeString()}
      </span>
    </div>
  );
};
